'use client';

import React, { useRef } from 'react';
import gsap from 'gsap';
import { useGSAP } from '@gsap/react';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import LottieRender from './LottieRender';

gsap.registerPlugin(ScrollTrigger);

const MobileMissionValues = () => {
    const containerRef = useRef<HTMLElement>(null);

    useGSAP(() => {
        // Animation for cards entering
        const cards = gsap.utils.toArray<HTMLElement>('.mission-card');
        cards.forEach((card, i) => {
            gsap.from(card, {
                scrollTrigger: {
                    trigger: card,
                    start: 'top 90%',
                    end: 'top 60%',
                    scrub: 1
                },
                y: 50,
                opacity: 0,
                ease: 'power3.out'
            });
        });
    }, { scope: containerRef });

    return (
        <section ref={containerRef} className="relative bg-slate-50 min-h-[140vh]">
            {/* Sticky Background Animation */}
            <div className="sticky top-0 h-[60vh] w-full overflow-hidden flex items-center justify-center opacity-40 grayscale-[20%]">
                <div className="w-full h-full transform scale-125">
                    <LottieRender animationPath="/animations/mission_v2.json" />
                </div>
                {/* Gradient Fade to blend with content at bottom */}
                <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-slate-50 to-transparent" />
            </div>

            {/* Scrollable Overlay Content */}
            <div className="relative z-10 px-6 pb-24 -mt-[20vh] space-y-6">

                {/* Header Card */}
                <div className="mission-card p-4">
                    <span className="text-xs font-bold text-indigo-600 tracking-[0.2em] uppercase block mb-4">
                        Our Mission
                    </span>
                    <h2 className="text-4xl font-black text-slate-900 leading-[1.1]">
                        Engineering the <br />
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-violet-600">
                            Digital Pulse
                        </span> <br />
                        of Tomorrow
                    </h2>
                </div>

                {/* Description Card */}
                <div className="mission-card p-4">
                    <p className="text-lg text-slate-800 leading-relaxed font-bold">
                        We don't just build websites; we construct living digital organisms. Our mission is to empower brands with self-sustaining, high-performance ecosystems that adapt and grow.
                    </p>
                </div>

                {/* Values Stack */}
                <div className="space-y-4 pt-4">
                    {[
                        { title: "Innovation First", desc: "Pioneering new standards." },
                        { title: "Transparent Process", desc: "Clear at every step." },
                        { title: "Scalable Growth", desc: "Built for the future." }
                    ].map((item, i) => (
                        <div key={i} className="mission-card flex items-center p-5 rounded-2xl border border-slate-900/5 bg-white/20 backdrop-blur-sm">
                            <div className="flex-shrink-0 w-12 h-12 rounded-full bg-white/50 flex items-center justify-center mr-4 text-indigo-700">
                                <span className="font-bold text-lg">{i + 1}</span>
                            </div>
                            <div>
                                <h3 className="font-bold text-slate-900">{item.title}</h3>
                                <p className="text-xs text-slate-600 font-bold uppercase tracking-wide">{item.desc}</p>
                            </div>
                        </div>
                    ))}
                </div>

            </div>
        </section>
    );
};

export default MobileMissionValues;
